import React, {useState, useEffect, useRef} from 'react';
import {Checkbox, FormControlLabel, Popover, Typography} from '@mui/material';
import './FileTypeSelector.scss';
import ArrFilterChip from '../arr-filter-chip/ArrFilterChip';
import {t} from 'i18next';
import ArrButton from '../arr-button/ArrButton';
import {FileTypeItem} from '../../../api/types/production.type';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {useDropdownKeyboardNavigation} from '../../../hooks/utils/useDropdownKeyboardNavigation';

interface FileTypeSelectorProps {
  fileGroupList?: FileTypeItem[];
  onSelectionChange?: (selectedIds: string[]) => void;
  disabled?: boolean;
  checkboxDataTestId?: string;
}

const FileTypeSelector: React.FC<FileTypeSelectorProps> = ({
  fileGroupList = [],
  onSelectionChange,
  disabled = false,
  checkboxDataTestId = 'file-type-selector',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedChips, setSelectedChips] = useState<string[]>([]);
  const [shouldOpenOnFocus, setShouldOpenOnFocus] = useState(true);
  const firstItemRef = useRef<HTMLInputElement | null>(null);

  // Use API data only
  const availableFileTypes = fileGroupList.map((item) => ({
    label: item.fileTypeLabel,
    value: item.fileTypeID.toString(),
    isSelected: item.isFileTypeSelected,
  }));

  // Initialize selected items from API data
  useEffect(() => {
    const preSelected = fileGroupList
      .filter((item) => item.isFileTypeSelected)
      .map((item) => item.fileTypeID.toString());
    setSelected(preSelected);
    setSelectedChips(preSelected);
  }, [fileGroupList]);

  // Notify parent component when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selected);
    }
  }, [selected, onSelectionChange]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setShouldOpenOnFocus(false);
    setAnchorEl(null);
    setTimeout(() => {
      setShouldOpenOnFocus(true);
    }, 100);
  };

  const handleApply = () => {
    setShouldOpenOnFocus(false);
    setSelectedChips(selected);
    setAnchorEl(null);
  };

  const handleToggle = (value: string) => {
    if (disabled) return;
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleRemoveChips = (value: string) => {
    if (disabled) return;
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setSelectedChips((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };
  const handleSelectAll = () => {
    if (disabled) return;
    if (selected.length === availableFileTypes.length) {
      setSelected([]);
    } else {
      setSelected(availableFileTypes.map((f) => f.value));
    }
  };

  const handleClearAll = () => {
    if (disabled) return;
    setSelected([]);
  };

  const open = Boolean(anchorEl);

  const {handleKeyDown} = useDropdownKeyboardNavigation({
    items: availableFileTypes,
    onSelect: (item) => handleToggle(item.value),
    isOpen: open,
    initialIndex: 0,
  });

  useEffect(() => {
    if (open && firstItemRef.current) {
      firstItemRef.current?.focus();
    }
  }, [open]);

  return (
    <div
      className="filetype-selector"
      data-testid={`file-type-selector-file-type-selecte-file-type-clear-all-apply`}
    >
      <div
        className="filetype-header"
        data-testid={`file-type-selector-file-type-selecte-file-type`}
      >
        <Typography
          variant="subtitle2"
          className="typo-text subhead5_size filetype-title"
          data-testid="filetype-header"
        >
          {t(
            'productionsAndExports.createNewProduction.productionComponent.fileType'
          )}
        </Typography>
        <ArrButton
          variant="primary"
          size="small"
          endIcon={<ArrowDropDownIcon />}
          onClick={handleClick}
          onFocus={(e) => {
            if (!disabled && shouldOpenOnFocus && !open)
              handleClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
          }}
          dataTestId="filetype-button"
          disabled={disabled}
        >
          {t(
            'productionsAndExports.createNewProduction.productionComponent.selecteFileType'
          )}
        </ArrButton>
      </div>
      <div
        className="selected-types"
        data-testid={`file-type-selector-selected-chips-wrapper`}
      >
        {selectedChips.map((type, index) => {
          const fileType = availableFileTypes.find((f) => f.value === type);
          const label = fileType?.label.split(' ')[0] || type;
          return (
            <ArrFilterChip
              key={type}
              classNames="chip"
              filterName=""
              selections={[{id: type, value: label}]}
              variant="v1"
              onRemove={() => handleRemoveChips(type)}
              disabled={disabled}
              dataTestId={`selected-chip-${index}`}
            />
          );
        })}
      </div>
      <Popover
        anchorEl={anchorEl}
        open={open}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onClose={(event, reason) => {
          handleClose();
        }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{
          className: 'filetype-menu',
          onKeyDown: handleKeyDown,
        }}
      >
        <div
          className="menu-content"
          data-testid={`file-type-selector-wrapper-${checkboxDataTestId}`}
        >
          <FormControlLabel
          data-testid={`file-type-selector-wrapper-label-${checkboxDataTestId}-select-all`}
            control={
              <Checkbox
                data-testid={`${checkboxDataTestId}-select-all`}
                checked={selected.length === availableFileTypes.length}
                indeterminate={
                  selected.length > 0 &&
                  selected.length < availableFileTypes.length
                }
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />

          <div
            className="filetype-list"
            data-testid={`file-type-selector-available-file-types-wrapper`}
          >
            {availableFileTypes.map((type, index) => (
              <FormControlLabel
               data-testid={`file-type-selector-available-${checkboxDataTestId}-option-${type.value}`}
                key={type.value}
                control={
                  <Checkbox
                    data-testid={`${checkboxDataTestId}-option-${type.value}`}
                    checked={selected.includes(type.value)}
                    onChange={() => handleToggle(type.value)}
                  />
                }
                label={type.label}
              />
            ))}
          </div>

          <div
            className="menu-footer"
            data-testid={`file-type-selector-clear-all-wrapper`}
          >
            <ArrButton
              variant="text"
              size="small"
              onClick={() => {
                handleClearAll();
              }}
              dataTestId="clear-all-button"
            >
              {t('common.button.clearAll')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="small"
              onClick={() => handleApply()}
              dataTestId="apply-button"
            >
              {t('common.button.apply')}
            </ArrButton>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default FileTypeSelector;
